import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, Download, ArrowDownToLine, Server, Database, Trash2, Play, 
  Sparkles, Bot, User, Send, Eye, Save, X, Loader2, CheckCircle2, XCircle, RefreshCw
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const TallyFetchPage = () => {
  const [tallyConnections, setTallyConnections] = useState([]);
  const [dbConnections, setDbConnections] = useState([]);
  const [dataTypes, setDataTypes] = useState({ masters: [], transactions: [] });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState('config'); // 'config' or 'ai'

  // Form state
  const [fetchForm, setFetchForm] = useState({
    name: '',
    tally_connection_id: '',
    db_connection_id: '',
    data_category: 'master',
    data_type: 'ledgers',
    date_from: '',
    date_to: '',
    company_name: '',
    custom_xml: '',
    table_name: '',
    sync_mode: 'incremental'
  });

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tallyRes, dbRes, typesRes, jobsRes] = await Promise.all([
        apiService.getTallyConnections(),
        apiService.getDBConnections(),
        apiService.getDataTypes(),
        apiService.getJobs()
      ]);
      setTallyConnections(tallyRes.data);
      setDbConnections(dbRes.data);
      setDataTypes(typesRes.data);
      setJobs(jobsRes.data.filter(j => j.job_type === 'fetch'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openBuilder = () => {
    const newSessionId = `fetch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setChatMessages([{
      role: 'assistant',
      content: "Hello! I can help you configure a Tally data fetch. Tell me what you need:\n\n• What type of data? (Ledgers, Stock Items, Sales, Purchases, etc.)\n• Date range for transactions?\n• Target database and table name?\n• Full sync or incremental?\n\nFor example: \"Fetch all sales vouchers for last 30 days\""
    }]);
    setFetchForm({
      name: '',
      tally_connection_id: tallyConnections[0]?.id || '',
      db_connection_id: dbConnections[0]?.id || '',
      data_category: 'master',
      data_type: 'ledgers',
      date_from: '',
      date_to: '',
      company_name: '',
      custom_xml: '',
      table_name: '',
      sync_mode: 'incremental'
    });
    setPreviewData(null);
    setIsBuilderOpen(true);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setChatMessages([]);
    setPreviewData(null);
  };

  const handlePreview = async () => {
    if (!fetchForm.tally_connection_id) {
      toast.error('Please select a Tally connection');
      return;
    }

    setPreviewLoading(true);
    try {
      const result = await apiService.previewFetch(fetchForm);
      if (result.data.success) {
        setPreviewData(result.data);
        toast.success(`Found ${result.data.sample_records?.length || 0} sample records`);
      } else {
        toast.error(result.data.error || 'Preview failed');
        setPreviewData({ error: result.data.error, request_xml: result.data.request_xml });
      }
    } catch (error) {
      toast.error('Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateJob = async () => {
    if (!fetchForm.name.trim()) {
      toast.error('Please enter a job name');
      return;
    }
    if (!fetchForm.tally_connection_id || !fetchForm.db_connection_id) {
      toast.error('Please select both Tally and Database connections');
      return;
    }

    try {
      await apiService.createJob({
        name: fetchForm.name,
        job_type: 'fetch',
        tally_connection_id: fetchForm.tally_connection_id,
        db_connection_id: fetchForm.db_connection_id,
        fetch_config: {
          data_category: fetchForm.data_category,
          data_type: fetchForm.data_type,
          date_from: fetchForm.date_from,
          date_to: fetchForm.date_to,
          company_name: fetchForm.company_name,
          custom_xml: fetchForm.custom_xml,
          table_name: fetchForm.table_name || `tally_${fetchForm.data_type}`,
          sync_mode: fetchForm.sync_mode
        },
        schedule_type: 'manual',
        alert_enabled: true,
        alert_recipients: []
      });
      toast.success('Fetch job created');
      loadData();
      closeBuilder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const runFetchJob = async (jobId) => {
    try {
      await apiService.runJob(jobId);
      toast.success('Fetch job started');
      loadData();
    } catch (error) {
      toast.error('Failed to run job');
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await apiService.sendAIMessage({
        session_id: sessionId,
        message: userMessage,
        context_type: 'fetch'
      });

      const aiResponse = response.data;
      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse.response }]);

      if (aiResponse.xml_content) {
        setFetchForm(prev => ({ ...prev, custom_xml: aiResponse.xml_content }));
        toast.success('Custom XML generated');
      }
    } catch (error) {
      toast.error('AI service error');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please configure manually.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="badge-success"><CheckCircle2 className="h-3 w-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge className="badge-error"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'running':
        return <Badge className="badge-warning"><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Running</Badge>;
      default:
        return <Badge className="badge-neutral">-</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Builder View
  if (isBuilderOpen) {
    return (
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="fetch-builder">
        {/* Builder Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={closeBuilder}>
              <X className="h-4 w-4 mr-2" />Close
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <h2 className="font-heading font-semibold text-lg">New Tally Fetch Job</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <Button
                variant={builderMode === 'config' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBuilderMode('config')}
                className={builderMode === 'config' ? 'bg-teal-700 hover:bg-teal-600' : ''}
              >
                <Server className="h-4 w-4 mr-2" />Configure
              </Button>
              <Button
                variant={builderMode === 'ai' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBuilderMode('ai')}
                className={builderMode === 'ai' ? 'bg-teal-700 hover:bg-teal-600' : ''}
              >
                <Sparkles className="h-4 w-4 mr-2" />AI Assist
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={handlePreview} disabled={previewLoading} data-testid="preview-fetch-btn">
              {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Preview
            </Button>
            <Button className="bg-teal-700 hover:bg-teal-600" size="sm" onClick={handleCreateJob} data-testid="create-fetch-job-btn">
              <Save className="h-4 w-4 mr-2" />Create Job
            </Button>
          </div>
        </div>

        {/* Builder Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel */}
          <div className="w-2/5 border-r border-slate-200 flex flex-col bg-white">
            {/* Basic Config */}
            <div className="p-4 border-b border-slate-200 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Job Name</Label>
                <Input
                  placeholder="e.g., Daily Ledger Sync"
                  value={fetchForm.name}
                  onChange={(e) => setFetchForm({ ...fetchForm, name: e.target.value })}
                  className="h-9"
                  data-testid="fetch-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Tally Connection</Label>
                  <Select value={fetchForm.tally_connection_id} onValueChange={(v) => setFetchForm({ ...fetchForm, tally_connection_id: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {tallyConnections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Target Database</Label>
                  <Select value={fetchForm.db_connection_id} onValueChange={(v) => setFetchForm({ ...fetchForm, db_connection_id: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {dbConnections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.db_type})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {builderMode === 'config' ? (
              /* Configuration Mode */
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Data Category</Label>
                    <Select value={fetchForm.data_category} onValueChange={(v) => {
                      const firstType = v === 'master' ? dataTypes.masters[0]?.value : dataTypes.transactions[0]?.value;
                      setFetchForm({ ...fetchForm, data_category: v, data_type: firstType || '' });
                    }}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="master">Masters (Ledgers, Stock Items, etc.)</SelectItem>
                        <SelectItem value="transaction">Transactions (Sales, Purchase, etc.)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Data Type</Label>
                    <Select value={fetchForm.data_type} onValueChange={(v) => setFetchForm({ ...fetchForm, data_type: v, table_name: `tally_${v}` })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(fetchForm.data_category === 'master' ? dataTypes.masters : dataTypes.transactions).map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {fetchForm.data_category === 'transaction' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Date From</Label>
                        <Input type="date" value={fetchForm.date_from} onChange={(e) => setFetchForm({ ...fetchForm, date_from: e.target.value })} className="h-9" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Date To</Label>
                        <Input type="date" value={fetchForm.date_to} onChange={(e) => setFetchForm({ ...fetchForm, date_to: e.target.value })} className="h-9" />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs">Company Name (Optional)</Label>
                    <Input placeholder="Leave empty to use connection default" value={fetchForm.company_name} onChange={(e) => setFetchForm({ ...fetchForm, company_name: e.target.value })} className="h-9" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Target Table Name</Label>
                    <Input value={fetchForm.table_name || `tally_${fetchForm.data_type}`} onChange={(e) => setFetchForm({ ...fetchForm, table_name: e.target.value })} className="h-9 font-mono text-sm" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Sync Mode</Label>
                    <Select value={fetchForm.sync_mode} onValueChange={(v) => setFetchForm({ ...fetchForm, sync_mode: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="incremental">Incremental (only new/updated)</SelectItem>
                        <SelectItem value="full">Full Sync (replace all)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Custom XML (Optional)</Label>
                    <Textarea
                      placeholder="Leave empty to use default template"
                      value={fetchForm.custom_xml}
                      onChange={(e) => setFetchForm({ ...fetchForm, custom_xml: e.target.value })}
                      className="h-32 font-mono text-xs"
                    />
                  </div>
                </div>
              </ScrollArea>
            ) : (
              /* AI Chat Mode */
              <>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}`}>
                          <div className="flex items-start gap-2">
                            {msg.role === 'assistant' && <Bot className="h-4 w-4 mt-0.5 text-teal-600 flex-shrink-0" />}
                            <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                            {msg.role === 'user' && <User className="h-4 w-4 mt-0.5 text-slate-500 flex-shrink-0" />}
                          </div>
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="chat-bubble-ai">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-teal-600" />
                            <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-4 border-t border-slate-200">
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., 'Fetch all sales for last month'"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                      disabled={chatLoading}
                      className="flex-1"
                    />
                    <Button className="bg-teal-700 hover:bg-teal-600" onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 flex flex-col bg-slate-50">
            <div className="p-4 border-b border-slate-200 bg-white">
              <Label className="text-xs font-medium">Data Preview</Label>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {previewData ? (
                previewData.error ? (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <p className="text-red-800 font-medium">Error</p>
                      <p className="text-sm text-red-600 mt-1">{previewData.error}</p>
                      {previewData.request_xml && (
                        <pre className="mt-3 p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-auto max-h-40">{previewData.request_xml}</pre>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                      <Badge className="badge-success">Found {previewData.sample_records?.length || 0} records</Badge>
                      <span className="text-slate-500">{previewData.columns?.length || 0} columns detected</span>
                    </div>
                    <Card>
                      <CardContent className="p-0">
                        <ScrollArea className="h-[400px]">
                          <Table>
                            <TableHeader>
                              <TableRow className="table-header">
                                {previewData.columns?.slice(0, 8).map((col) => (
                                  <TableHead key={col} className="table-cell font-mono text-xs">{col}</TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {previewData.sample_records?.map((row, idx) => (
                                <TableRow key={idx} className="table-row">
                                  {previewData.columns?.slice(0, 8).map((col) => (
                                    <TableCell key={col} className="table-cell text-xs font-mono truncate max-w-32">
                                      {row[col]?.toString() || '-'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </div>
                )
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <ArrowDownToLine className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Click "Preview" to fetch sample data</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="page-container" data-testid="tally-fetch-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Tally Data Fetch</h1>
          <p className="text-sm text-slate-500 mt-1">Fetch data from Tally and sync to your database</p>
        </div>
        <Button 
          className="bg-teal-700 hover:bg-teal-600"
          onClick={openBuilder}
          disabled={tallyConnections.length === 0 || dbConnections.length === 0}
          data-testid="create-fetch-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Fetch Job
        </Button>
      </div>

      {(tallyConnections.length === 0 || dbConnections.length === 0) && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Prerequisites Missing</p>
                <p className="text-sm text-amber-700">
                  You need at least one Tally connection and one database connection to create fetch jobs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead className="table-cell">Job Name</TableHead>
                <TableHead className="table-cell">Data Type</TableHead>
                <TableHead className="table-cell">Target Table</TableHead>
                <TableHead className="table-cell">Sync Mode</TableHead>
                <TableHead className="table-cell">Last Run</TableHead>
                <TableHead className="table-cell">Status</TableHead>
                <TableHead className="table-cell text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Download className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No fetch jobs created yet</p>
                    <p className="text-sm">Click "New Fetch Job" to start syncing data from Tally</p>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.id} className="table-row" data-testid={`fetch-job-row-${job.id}`}>
                    <TableCell className="table-cell font-medium">{job.name}</TableCell>
                    <TableCell className="table-cell">
                      <Badge className="badge-neutral">{job.fetch_config?.data_type || '-'}</Badge>
                    </TableCell>
                    <TableCell className="table-cell font-mono text-sm">{job.fetch_config?.table_name || '-'}</TableCell>
                    <TableCell className="table-cell">
                      <Badge className={job.fetch_config?.sync_mode === 'incremental' ? 'badge-success' : 'badge-warning'}>
                        {job.fetch_config?.sync_mode || 'incremental'}
                      </Badge>
                    </TableCell>
                    <TableCell className="table-cell text-sm text-slate-500">
                      {job.last_run ? format(parseISO(job.last_run), 'MMM d, HH:mm') : '-'}
                    </TableCell>
                    <TableCell className="table-cell">{getStatusBadge(job.last_status)}</TableCell>
                    <TableCell className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => runFetchJob(job.id)} className="text-teal-600 hover:bg-teal-50">
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => apiService.deleteJob(job.id).then(loadData)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TallyFetchPage;
